/**
 * Monitors storage: DB size, entity breakdown, available space.
 */

import {
  getDatabaseSize,
  getEntityBreakdown,
  initSqliteStore,
} from '../offline/sqlite-store.service';
import type { EntityBreakdownRow } from '../offline/sqlite-store-types';

export interface StorageInfo {
  databaseSizeBytes: number;
  entityBreakdown: EntityBreakdownRow[];
  availableBytes: number | null;
  usageBytes: number | null;
  quotaBytes: number | null;
}

async function getAvailableSpace(): Promise<{
  availableBytes: number | null;
  usageBytes: number | null;
  quotaBytes: number | null;
}> {
  if (typeof navigator?.storage?.estimate !== 'function') {
    return { availableBytes: null, usageBytes: null, quotaBytes: null };
  }
  try {
    const estimate = await navigator.storage.estimate();
    const usage = estimate.usage ?? null;
    const quota = estimate.quota ?? null;
    const available = quota != null && usage != null ? quota - usage : null;
    return {
      availableBytes: available ?? null,
      usageBytes: usage ?? null,
      quotaBytes: quota ?? null,
    };
  } catch {
    return { availableBytes: null, usageBytes: null, quotaBytes: null };
  }
}

export async function getStorageInfo(): Promise<StorageInfo> {
  await initSqliteStore();
  const [dbSize, breakdown, { availableBytes, usageBytes, quotaBytes }] =
    await Promise.all([
      getDatabaseSize(),
      getEntityBreakdown(),
      getAvailableSpace(),
    ]);

  return {
    databaseSizeBytes: dbSize.sizeBytes,
    entityBreakdown: breakdown,
    availableBytes,
    usageBytes,
    quotaBytes,
  };
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
